import { RefObject, useCallback, useMemo, useRef, useState } from 'react';
import { gridColumnFieldsSelector, gridColumnVisibilityModelSelector } from '@mui/x-data-grid-pro';
import type { GridApiPro, GridColDef, GridColumnResizeParams, GridColumnVisibilityModel, GridValidRowModel } from '@mui/x-data-grid-pro';
import { useDispatch, useSelector } from '@cbridge-utils/react-redux';
import { parseJsonSetting, UserSettingsAction, userSettingSelector } from '@cbridge-packages/user-settings';
import {
  COLUMNS_SETTING_SUFFIX, applyColumnsSetting, isColumnsSetting, isUserField, toColumnVisibilityModel, toColumnsSetting,
} from '../../_helpers/columnsSetting';

/**
 * Per-user persistence of column order, visibility and width under `{settingsKey}.datagrid.columns`
 * (LB-4081, generalised to every grid in LB-4992).
 *
 * The saved state is frozen at mount and applied as a pure transform of the `columns` prop, so it
 * also covers columns that arrive after the first render (Superset reports); later saves do not
 * feed back into the grid (it owns its state within the session, persistence is write-through).
 * Nothing is read or written without a key.
 */
export const useColumnsSettings = (apiRef: RefObject<GridApiPro | null>, settingsKey?: string) => {
  const dispatch = useDispatch();
  const settingKey = settingsKey ? `${settingsKey}${COLUMNS_SETTING_SUFFIX}` : '';
  const savedRaw = useSelector(userSettingSelector, { settingKey });
  const [saved] = useState(() => parseJsonSetting(savedRaw, isColumnsSetting));

  // Accumulates user resizes on top of the saved ones; the grid state itself keeps no
  // trace of which columns were resized explicitly, so they are tracked here.
  const widthsRef = useRef<Record<string, number>>({ ...saved?.widths });

  const orderColumns = useCallback(
    <T extends GridValidRowModel>(columns: readonly GridColDef<T>[]) => applyColumnsSetting(columns, saved),
    [saved],
  );

  const columnVisibilityModel = useMemo(() => toColumnVisibilityModel(saved), [saved]);

  const saveColumnsState = useCallback((visibilityModel?: GridColumnVisibilityModel) => {
    if (!settingKey || !apiRef.current) {
      return;
    }

    const setting = toColumnsSetting(
      gridColumnFieldsSelector(apiRef),
      visibilityModel ?? gridColumnVisibilityModelSelector(apiRef),
      widthsRef.current,
    );

    dispatch(UserSettingsAction.saveSetting({ key: settingKey, value: JSON.stringify(setting) }));
  }, [apiRef, dispatch, settingKey]);

  const handleColumnWidthChange = useCallback((params: GridColumnResizeParams) => {
    if (isUserField(params.colDef.field)) {
      widthsRef.current[params.colDef.field] = params.width;
    }
    saveColumnsState();
  }, [saveColumnsState]);

  return { orderColumns, columnVisibilityModel, saveColumnsState, handleColumnWidthChange };
};
